import React, { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Alert,
} from "@mui/material";
import InfoPopUp from "../../Common/InfoPopUp";
import InfoIcon from "@mui/icons-material/Info";
import {
  STATUS_CONFIG,
  ACCREDITATION_TYPES,
  CERTIFICATION_TYPES,
  ENTITY_TYPES,
  formatCurrency,
  formatDate,
  formatFileSize,
} from "./utils";

const AccreditedStatus = ({ statusData }) => {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const theme = localStorage.getItem("theme") || "light";

  // Theme colors matching the DocumentUpload component
  const darkColors = {
    background: "#121212",
    paper: "#1e1e1e",
    card: "#1a1a1a",
    text: "#e0e0e0",
    textSecondary: "#aaa",
    border: "#333",
    primary: "#0d6efd",
    success: "#28a745",
    info: "#0dcaf0",
    warning: "#ffc107",
    error: "#dc3545",
  };

  const lightColors = {
    background: "#f8f9fa",
    paper: "#ffffff",
    card: "#f8f9fa",
    text: "#212529",
    textSecondary: "#6c757d",
    border: "#dee2e6",
    primary: "#1976d2",
    success: "#2e7d32",
    info: "#0288d1",
    warning: "#ed6c02",
    error: "#d32f2f",
  };

  const colors = theme === "dark" ? darkColors : lightColors;

  if (!statusData) return null;

  const status = statusData.status || "PENDING";
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  const accreditationType = statusData.accreditationType;
  const typeInfo = ACCREDITATION_TYPES[accreditationType];

  const getCertTypeLabel = (value) =>
    CERTIFICATION_TYPES.find((ct) => ct.value === value)?.label || value;

  const getEntityTypeLabel = (value) =>
    ENTITY_TYPES.find((et) => et.value === value)?.label || value;

  // ─── Type-specific details renderer ───
  const renderTypeDetails = () => {
    switch (accreditationType) {
      case "INCOME":
        return (
          <>
            {statusData.annualIncome && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Annual Income
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {formatCurrency(
                    statusData.annualIncome,
                    statusData.incomeCurrency
                  )}
                </Typography>
              </Grid>
            )}
            {statusData.jointIncome && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Joint Income
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {formatCurrency(
                    statusData.jointIncome,
                    statusData.incomeCurrency
                  )}
                </Typography>
              </Grid>
            )}
          </>
        );
      case "NET_WORTH":
        return (
          statusData.netWorth && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                Net Worth
              </Typography>
              <Typography variant="body1" sx={{ color: colors.text }}>
                {formatCurrency(
                  statusData.netWorth,
                  statusData.netWorthCurrency
                )}
              </Typography>
            </Grid>
          )
        );
      case "PROFESSIONAL":
        return (
          <>
            {statusData.certificationType && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Certification
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {getCertTypeLabel(statusData.certificationType)}
                </Typography>
              </Grid>
            )}
            {statusData.certificationNumber && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  License Number
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {statusData.certificationNumber}
                </Typography>
              </Grid>
            )}
            {statusData.certificationIssuer && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Issuer
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {statusData.certificationIssuer}
                </Typography>
              </Grid>
            )}
            {statusData.certificationExpiry && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Expiry
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {formatDate(statusData.certificationExpiry)}
                </Typography>
              </Grid>
            )}
          </>
        );
      case "ENTITY":
        return (
          <>
            {statusData.entityName && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Entity Name
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {statusData.entityName}
                </Typography>
              </Grid>
            )}
            {statusData.entityType && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Entity Type
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {getEntityTypeLabel(statusData.entityType)}
                </Typography>
              </Grid>
            )}
            {statusData.entityAssets && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Total Assets
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {formatCurrency(statusData.entityAssets)}
                </Typography>
              </Grid>
            )}
            {statusData.entityJurisdiction && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Jurisdiction
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {statusData.entityJurisdiction}
                </Typography>
              </Grid>
            )}
          </>
        );
      default:
        return null;
    }
  };

  // ─── Document verification status chip color ───
  const getDocStatusChipColor = (verificationStatus) => {
    switch (verificationStatus) {
      case "VERIFIED":
        return "success";
      case "REJECTED":
        return "error";
      case "PENDING":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: colors.paper,
        color: colors.text,
        borderRadius: 2,
        boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      {/* ─── Header with Status ─── */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ color: colors.text, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            Accredited Investor Verification
            <InfoIcon
              sx={{
                color: colors.textSecondary,
                cursor: "pointer",
                ml: 1,
                fontSize: '1.2rem'
              }}
              onClick={() => setShow(true)}
            />
          </Typography>
          <Chip
            label={config.label}
            size="small"
            sx={{
              backgroundColor: config.color,
              color: "#fff",
              fontWeight: 600,
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: config.color, fontWeight: 500 }}>
          {config.message}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 1 }}>
          {config.description}
        </Typography>
      </Box>

      {/* ─── Rejection Reason ─── */}
      {status === "REJECTED" && statusData.rejectionReason && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: colors.error + (theme === 'dark' ? '20' : '10')
          }}
        >
          <strong>Rejection Reason:</strong> {statusData.rejectionReason}
        </Alert>
      )}

      {/* ─── Review Notes ─── */}
      {statusData.reviewNotes && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            backgroundColor: colors.info + (theme === 'dark' ? '20' : '10')
          }}
        >
          <strong>Review Notes:</strong> {statusData.reviewNotes}
        </Alert>
      )}

      {/* ─── Approval / Expiry Info ─── */}
      {status === "APPROVED" && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: colors.success + (theme === 'dark' ? '15' : '08'),
            borderRadius: 1,
            border: `1px solid ${colors.success + '30'}`,
          }}
        >
          <Grid container spacing={2}>
            {statusData.approvedAt && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Approved On
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {formatDate(statusData.approvedAt)}
                </Typography>
              </Grid>
            )}
            {statusData.expiresAt && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Expires On
                </Typography>
                <Typography variant="body1" sx={{ color: colors.text }}>
                  {formatDate(statusData.expiresAt)}
                </Typography>
              </Grid>
            )}
            {statusData.daysUntilExpiry !== null &&
              statusData.daysUntilExpiry !== undefined && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    Days Until Expiry
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color:
                        statusData.daysUntilExpiry < 30
                          ? colors.warning
                          : colors.success,
                      fontWeight: 600,
                    }}
                  >
                    {statusData.daysUntilExpiry} days
                  </Typography>
                </Grid>
              )}
          </Grid>
        </Box>
      )}

      <Divider sx={{ my: 2, borderColor: colors.border }} />

      {/* ─── Application Details ─── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: colors.text }}>
          Application Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Application ID
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text }}>
              #{statusData.applicationId}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Accreditation Type
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text }}>
              {typeInfo?.icon || ""}{" "}
              {statusData.accreditationTypeDescription ||
                typeInfo?.label ||
                accreditationType}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Verification Method
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text }}>
              {statusData.verificationMethodDescription ||
                statusData.verificationMethod}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Submitted On
            </Typography>
            <Typography variant="body1" sx={{ color: colors.text }}>
              {formatDate(statusData.createdAt)}
            </Typography>
          </Grid>

          {/* Type-specific details */}
          {renderTypeDetails()}
        </Grid>
      </Box>

      {/* ─── Uploaded Documents ─── */}
      {statusData.documents && statusData.documents.length > 0 && (
        <>
          <Divider sx={{ my: 2, borderColor: colors.border }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: colors.text }}>
              Documents ({statusData.documentCount || statusData.documents.length})
            </Typography>
            <Grid container spacing={2}>
              {statusData.documents.map((doc) => (
                <Grid size={{ xs: 12, md: 6 }} key={doc.documentId}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.card,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.textSecondary,
                          textTransform: "uppercase",
                          fontSize: "0.7rem"
                        }}
                      >
                        {doc.documentType?.replace(/_/g, " ")}
                      </Typography>
                      <Chip
                        label={doc.verificationStatus || "PENDING"}
                        size="small"
                        color={getDocStatusChipColor(doc.verificationStatus)}
                        sx={{ height: 20, fontSize: "0.65rem" }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.text }}>
                      📎 {doc.documentName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                      {formatFileSize(doc.documentSize)} •{" "}
                      {formatDate(doc.uploadedAt)}
                    </Typography>
                    {doc.verificationNotes && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.info,
                          display: "block",
                          mt: 0.5
                        }}
                      >
                        Note: {doc.verificationNotes}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      {/* ─── Info Popup ─── */}
      <InfoPopUp
        open={show}
        onClose={handleClose}
        title={"Accredited Investor Verification"}
        description={`Your application (ID: #${statusData.applicationId}) is currently ${config.label}.

Type: ${statusData.accreditationTypeDescription || accreditationType}
Method: ${statusData.verificationMethodDescription || statusData.verificationMethod}

The verification process typically takes 1-5 business days depending on the accreditation type. You will be notified via email once the review is complete.`}
      />
    </Paper>
  );
};

export default AccreditedStatus;